import { IInvoice, IInvoiceFilters, IInvoiceList } from '~/models/Invoices';
import { INVOICE_PRODUCT_TYPE_ENUM, INVOICE_PRODUCT_TYPE_NUM_ENUM } from '~/enums/Invoices.enum';
import { MySsException } from '~/types/MySsException';

export const useInvoicesStore = defineStore(
  'MY_SCAN_SOURCE_INVOICES_STORE',
  () => {
    const AUTH_STORE = useAuthStore();

    const { session } = storeToRefs(AUTH_STORE);

    const invoiceListPaginator = usePaginator();
    const {
      pagination: invoiceListPagination,
      CallPaginate: CallInvoiceListPaginate,
      CallSetPagination,
    } = invoiceListPaginator;

    const loadingInvoices = ref(false);
    const expiration = ref<Date | null>(null);
    const invoiceListFilters = ref<IInvoiceFilters>({
      ...INVOICE_LIST_FILTERS,
    });
    const invoiceList = ref<IInvoiceList>([]);
    const invoice = ref<IInvoice>(createInvoice());
    const noDataFiltered = ref(false);

    const hasFilteredData = computed(() =>
      noDataFiltered.value ? 'TOAST.DETAILS.NO_DATA_FILTERED' : '',
    );
    const isExpired = computed(() => {
      const validator = new Date().getTime();
      if (!expiration.value) return true;
      return expiration.value.getTime() < validator;
    });

    function setInvoiceList(data: {
      invoiceList: IInvoiceList;
      pagination: typeof PAGINATION;
    }) {
      data.invoiceList.forEach((invoice: IInvoice) => {
        invoice.productType =
          INVOICE_PRODUCT_TYPE_ENUM[INVOICE_PRODUCT_TYPE_NUM_ENUM[invoice.productType]];
      });
      invoiceList.value = data.invoiceList;
      CallSetPagination(data.pagination);
    }

    function resetFilters() {
      invoiceListFilters.value = { ...INVOICE_LIST_FILTERS };
    }

    function setExpirationTime() {
      const expirationDate = new Date();
      expirationDate.setMinutes(expirationDate.getMinutes() + 3);
      expiration.value = expirationDate;
    }

    async function dispatchGetInvoices(force = false): Promise<void> {
      loadingInvoices.value = true;
      if (!isExpired.value && !force) return;

      await listInvoices(session.value.currentEconomicGroupMember)
        .then((data) => {
          setInvoiceList(data);
          setExpirationTime();
        })
        .catch((error: MySsException) => {
          setInvoiceList({
            invoiceList: [],
            pagination: { ...PAGINATION },
          });

          throw error;
        })
        .finally(() => {
          loadingInvoices.value = false;
        });
    }

    function onSessionClose() {
      invoiceList.value = [];
      invoice.value = createInvoice();
      invoiceListFilters.value = { ...INVOICE_LIST_FILTERS };
      invoiceListPagination.value = { ...PAGINATION };
      loadingInvoices.value = false;
    }

    return {
      invoiceList,
      invoice,
      invoiceListFilters,
      invoiceListPagination,
      hasFilteredData,
      noDataFiltered,
      loadingInvoices,
      isExpired,
      setExpirationTime,
      CallInvoiceListPaginate,
      setInvoiceList,
      resetFilters,
      dispatchGetInvoices,
      onSessionOpen: dispatchGetInvoices,
      onSessionClose,
      onChangeEconomicGroup: dispatchGetInvoices,
    };
  },
  {
    persist: true,
  },
);
